import {
  FC,
  ReactNode,
  lazy,
  Suspense,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useLocation } from '@remix-run/react';
import useEmblaCarousel from 'embla-carousel-react';
import { productSizings } from '~/common/constants/dictionary';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import { useNavigation } from '~/common/hooks/useNavigation';
import { useProductDetails } from '~/common/hooks/useProductDetails';
import { useScrollWithBoundary } from '~/common/hooks/useScrollWithBoundary';
import { isBrowser } from '~/common/utils/browser';
import { BackgroundOverlay } from '~/components/backgroundOverlay';
import { Product } from '~/types/product';
import { CollectionByHandle } from '~/types/dto/collectionsDto';
import { getProductForColor } from '~/common/utils/product';
import {
  buildEcommerce,
  buildTrackingItemFromProduct,
  pushDataLayerEvent,
} from '~/common/utils/tracking';
import { TRACKING_EVENTS } from '~/common/constants/tracking';
import { trackKlaviyoViewedProduct } from '~/common/utils/klaviyo';
import { DesktopImageGallery } from './components/DesktopImageGallery';
import { MobileImageCarousel } from './components/MobileImageCarousel';
import { ThumbnailGallery } from './components/ThumbnailGallery';
import { ProductInfo } from './components/ProductInfo';
import { MatchWithContent } from './components/MatchWithContent';
import {
  PageContainer,
  ContentWrapper,
  ImageColumn,
  DetailsColumn,
  DetailsWrapper,
  DetailsContent,
  MatchWithContainer,
  MobileOnly,
} from './styles';

const SizeGuideDrawer = lazy(() => import('~/components/SizeGuideDrawer'));

interface IProductDetails {
  product: Product;
  productsByHandle: Array<Product>;
  matchingByProductId?: Record<string, Product[]>;
  collectionsByHandle?: CollectionByHandle[];
  /** Overrides the back button target. Defaults to the public shop. */
  onBackClick?: () => void;
}

const LazyLoad: FC<{ children: ReactNode }> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return <div ref={ref}>{isVisible ? children : null}</div>;
};

export const ProductDetails: FC<IProductDetails> = ({
  product,
  productsByHandle,
  matchingByProductId = {},
  collectionsByHandle,
  onBackClick,
}) => {
  const { goToShop } = useNavigation();
  const { pathname } = useLocation();
  const handleBackClick = onBackClick ?? goToShop;
  const { isMobile } = useMediaQuery();
  const urlParams = new URLSearchParams(
    isBrowser ? window.location.search : '',
  );

  const preselectedSize = urlParams.get('selectedSize') || '';

  useEffect(() => {
    if (isBrowser) {
      window.scrollTo(0, 0);
    }
  }, []);

  const {
    isSizingOpen,
    toggleSizingModal,
    filteredProductMedia,
    setProductSettings,
    productSettings,
    variants,
  } = useProductDetails(product, productsByHandle, preselectedSize);

  const { selectedColor } = productSettings;
  const activeProduct =
    getProductForColor(productsByHandle, product, selectedColor) ?? product;

  const lastViewItemIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastViewItemIdRef.current === activeProduct.id) return;
    lastViewItemIdRef.current = activeProduct.id;

    pushDataLayerEvent({
      event: TRACKING_EVENTS.viewItem,
      ecommerce: buildEcommerce(
        [buildTrackingItemFromProduct({ product: activeProduct })],
        activeProduct.prices.currencyCode,
      ),
    });

    // Shares this effect, and therefore `lastViewItemIdRef`, on purpose: no second
    // dedupe to keep in sync, and the GA4 and Klaviyo event counts stay structurally
    // identical, which is what makes the duplicate check verifiable.
    // `trackKlaviyoViewedProduct` suppresses the private-sale and secret-event
    // routes, which reach this same component.
    trackKlaviyoViewedProduct(activeProduct, pathname);
  }, [activeProduct, pathname]);
  const matching = matchingByProductId[activeProduct.id] ?? [];
  const productSizing = productSizings.find((variant) =>
    variant.type.includes(product.productType),
  );

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const pageScrollRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const detailsColumnRef = useRef<HTMLDivElement>(null);
  const thumbnailScrollTopRef = useRef(0);
  const lastImageIndexRef = useRef<number | null>(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const scrollToImage = useCallback((index: number) => {
    const container = pageScrollRef.current;
    const imageEl = imageRefs.current[index];
    if (!imageEl || !container) return;

    setCurrentImageIndex(index);

    const startScrollTop = container.scrollTop;
    const containerRect = container.getBoundingClientRect();
    const imageRect = imageEl.getBoundingClientRect();
    const targetScrollTop = Math.max(
      0,
      Math.min(
        container.scrollHeight - container.clientHeight,
        startScrollTop + (imageRect.top - containerRect.top),
      ),
    );

    if (Math.abs(targetScrollTop - startScrollTop) < 2) return;

    const duration = 400;
    const startTime = performance.now();
    const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);

      container.scrollTop =
        startScrollTop + (targetScrollTop - startScrollTop) * eased;
      if (t < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, []);

  const handlePrevImage = useCallback(() => {
    const prevIndex =
      currentImageIndex === 0
        ? filteredProductMedia.length - 1
        : currentImageIndex - 1;
    scrollToImage(prevIndex);
    emblaApi?.scrollTo(prevIndex);
  }, [currentImageIndex, filteredProductMedia.length, scrollToImage, emblaApi]);

  const handleNextImage = useCallback(() => {
    const nextIndex =
      currentImageIndex === filteredProductMedia.length - 1
        ? 0
        : currentImageIndex + 1;
    scrollToImage(nextIndex);
    emblaApi?.scrollTo(nextIndex);
  }, [currentImageIndex, filteredProductMedia.length, scrollToImage, emblaApi]);

  const handleThumbnailClick = useCallback(
    (index: number) => {
      scrollToImage(index);
      emblaApi?.scrollTo(index);
    },
    [scrollToImage, emblaApi],
  );

  const handleImageRef = useCallback(
    (index: number, el: HTMLDivElement | null) => {
      imageRefs.current[index] = el;
    },
    [],
  );

  useEffect(() => {
    const scrollContainer = pageScrollRef.current;
    if (!scrollContainer) return;

    let rafId: number;
    let tickScheduled = false;

    const runScrollTick = () => {
      tickScheduled = false;

      const containerRect = scrollContainer.getBoundingClientRect();
      const containerTop = containerRect.top;

      let closestIndex = 0;
      let closestDistance = Infinity;

      imageRefs.current.forEach((imageEl, index) => {
        if (imageEl) {
          const imageRect = imageEl.getBoundingClientRect();
          const distance = Math.abs(imageRect.top - containerTop);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        }
      });

      if (lastImageIndexRef.current !== closestIndex) {
        lastImageIndexRef.current = closestIndex;
        setCurrentImageIndex(closestIndex);
      }
    };

    const handleScroll = () => {
      if (tickScheduled) return;
      tickScheduled = true;
      rafId = requestAnimationFrame(runScrollTick);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [filteredProductMedia.length]);

  useScrollWithBoundary({
    scrollRef: detailsColumnRef,
    fallbackScrollRef: pageScrollRef,
    enabled: !isMobile,
  });

  const onEmblaSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentImageIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onEmblaSelect);
    return () => {
      emblaApi.off('select', onEmblaSelect);
    };
  }, [emblaApi, onEmblaSelect]);

  return (
    <PageContainer ref={pageScrollRef}>
      <Suspense fallback={null}>
        <SizeGuideDrawer
          toggleSizingModal={toggleSizingModal}
          productSizing={productSizing}
          isSizingOpen={isSizingOpen}
        />
      </Suspense>

      <ContentWrapper $galleryHeightVh={filteredProductMedia.length || 1}>
        <ImageColumn>
          <DesktopImageGallery
            media={filteredProductMedia}
            productTitle={product.title}
            currentIndex={currentImageIndex}
            onImageRef={handleImageRef}
          />

          <MobileImageCarousel
            media={filteredProductMedia}
            productTitle={product.title}
            currentIndex={currentImageIndex}
            emblaRef={emblaRef}
            onPrev={handlePrevImage}
            onNext={handleNextImage}
            onDotClick={handleThumbnailClick}
          />
        </ImageColumn>

        <DetailsColumn ref={detailsColumnRef}>
          <DetailsWrapper>
            <ThumbnailGallery
              media={filteredProductMedia}
              productTitle={product.title}
              currentIndex={currentImageIndex}
              onThumbnailClick={handleThumbnailClick}
              onPrev={handlePrevImage}
              onNext={handleNextImage}
              thumbnailScrollTopRef={thumbnailScrollTopRef}
            />

            <DetailsContent>
              <ProductInfo
                product={activeProduct}
                productSettings={productSettings}
                setProductSettings={setProductSettings}
                variants={variants}
                productSizing={productSizing}
                toggleSizingModal={toggleSizingModal}
                collectionsByHandle={collectionsByHandle}
                productsByHandle={productsByHandle}
                onBackClick={handleBackClick}
                matching={matching}
              />
            </DetailsContent>
          </DetailsWrapper>
        </DetailsColumn>

        {matching.length > 0 && (
          <MobileOnly>
            <LazyLoad>
              <MatchWithContainer>
                <MatchWithContent matching={matching} />
              </MatchWithContainer>
            </LazyLoad>
          </MobileOnly>
        )}
      </ContentWrapper>

      <BackgroundOverlay
        isOpen={isSizingOpen}
        onClick={toggleSizingModal}
        blur={true}
      />
    </PageContainer>
  );
};
